import { ReactNode } from "react";
import { useAuth } from "../hooks/use-auth";
import { LoginPage } from "./LoginPage";

interface AuthGateProps {
  children: ReactNode;
}

/**
 * Conditional rendering gate for authentication.
 * - When auth is disabled: renders children directly
 * - When auth is enabled AND user is authenticated: renders children
 * - When auth is enabled AND user is NOT authenticated: renders LoginPage
 * - While loading: renders a loading spinner
 */
export function AuthGate({ children }: AuthGateProps) {
  const { isAuthEnabled, isAuthLoading, firebaseUser } = useAuth();

  // If auth is disabled, render children directly
  if (!isAuthEnabled) {
    return <>{children}</>;
  }

  // Show loading state while checking auth
  if (isAuthLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    );
  }

  // If not authenticated, show login page
  if (!firebaseUser) {
    return <LoginPage />;
  }

  // User is authenticated, render the app
  return <>{children}</>;
}
